import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiValidateWord } from '../utils/api';
import SeoContent from '../components/SeoContent';
import PageSEO from '../components/PageSEO';
import ShareModal from '../components/ShareModal';

const STARTER_WORDS = [
  'APPLE', 'BRAVE', 'CRANE', 'DANCE', 'EAGLE', 'FLAME', 'GRAPE',
  'HORSE', 'IMAGE', 'JUICE', 'KNIFE', 'LEMON', 'MAPLE', 'NOBLE',
  'OCEAN', 'PIANO', 'QUEEN', 'RAVEN', 'STONE', 'TIGER', 'UNCLE',
  'VOICE', 'WHALE', 'XENON', 'YACHT', 'ZEBRA',
];

export default function WordChain() {
  const [chain, setChain]         = useState([]);
  const [current, setCurrent]     = useState('');
  const [input, setInput]         = useState('');
  const [status, setStatus]       = useState('idle'); // idle | playing | ended
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [used, setUsed]           = useState(new Set());
  const [showShare, setShowShare] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (status === 'playing') inputRef.current?.focus();
  }, [status, current]);

  function startGame() {
    const starter = STARTER_WORDS[Math.floor(Math.random() * STARTER_WORDS.length)];
    setChain([starter]);
    setCurrent(starter);
    setUsed(new Set([starter.toLowerCase()]));
    setInput('');
    setError('');
    setShowShare(false);
    setStatus('playing');
  }

  const requiredLetter = current ? current[current.length - 1] : '';

  async function handleSubmit(e) {
    e.preventDefault();
    const word = input.trim().toUpperCase();
    if (!word) return;

    if (word[0] !== requiredLetter) {
      setError(`Word must start with "${requiredLetter}"`);
      return;
    }
    if (word.length < 3) {
      setError('Word must be at least 3 letters');
      return;
    }
    if (used.has(word.toLowerCase())) {
      setError('Word already used in this chain!');
      return;
    }

    setLoading(true); setError('');
    try {
      const { data } = await apiValidateWord(word.toLowerCase());
      if (!data.valid) {
        setError(`"${word}" is not a valid English word`);
        return;
      }
      setChain(prev => [...prev, word]);
      setCurrent(word);
      setUsed(prev => new Set([...prev, word.toLowerCase()]));
      setInput('');
    } catch {
      setError('Could not validate word. Check connection.');
    } finally { setLoading(false); }
  }

  function giveUp() {
    setStatus('ended');
    setTimeout(() => setShowShare(true), 1000);
  }

  return (
    <div className="game-page">
      <PageSEO page="word-chain" />
      {showShare && (() => {
        const preview = chain.slice(0, 5).join(' → ') + (chain.length > 5 ? ' → …' : '');
        const shareText = `⛓️ WordZingo Word Chain\nI built a ${chain.length}-word chain!\n${preview}\n🎯 Can you beat ${chain.length} words? wordzingo.com/word-chain`;
        return (
          <ShareModal
            shareText={shareText}
            gameUrl="https://wordzingo.com/word-chain"
            onClose={() => setShowShare(false)}
            onNewGame={startGame}
          />
        );
      })()}
      <div className="container" style={{maxWidth:650}}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">⛓️</span>
          <h1>Word Chain</h1>
          <p>Each word must start with the last letter of the previous word. How long can you chain?</p>
        </div>

        {status === 'idle' && (
          <div className="solver-card text-center">
            <div style={{fontSize:'4rem'}}>⛓️</div>
            <h3 className="fw-bold">Build the Longest Chain!</h3>
            <div className="p-3 rounded-3 mb-3" style={{background:'var(--surface)', textAlign:'left'}}>
              <div className="fw-bold mb-2">How to play:</div>
              <ul className="text-muted mb-0">
                <li>A starting word is given to you</li>
                <li>Enter any valid word starting with the <strong>last letter</strong> of the current word</li>
                <li>Words must be real English words (3+ letters)</li>
                <li>You can't reuse a word already in the chain</li>
                <li>Chain as long as you can!</li>
              </ul>
            </div>
            <div className="p-3 rounded-3 mb-3" style={{background:'#f0fdf4', textAlign:'left'}}>
              <div className="fw-bold small text-success">Example chain:</div>
              <div className="fw-bold mt-1" style={{letterSpacing:2, color:'var(--primary)'}}>
                APPLE → EAGLE → ELECT → TIGER → RAVEN → …
              </div>
            </div>
            <button className="solve-btn" onClick={startGame}>🚀 Start Game</button>
          </div>
        )}

        {(status === 'playing' || status === 'ended') && (
          <>
            <div className="solver-card mb-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <span className="text-muted small fw-bold">CHAIN LENGTH</span>
                  <div style={{fontSize:'2rem', fontWeight:900, color:'var(--primary)', fontFamily:'Fredoka One,cursive', lineHeight:1}}>
                    {chain.length}
                  </div>
                </div>
                {status === 'playing' && (
                  <div className="text-center">
                    <div className="text-muted small fw-bold">NEXT MUST START WITH</div>
                    <div style={{
                      width:64, height:64, borderRadius:12,
                      background:'var(--primary)', color:'#fff',
                      fontSize:'2.2rem', fontWeight:900, fontFamily:'Fredoka One,cursive',
                      display:'flex', alignItems:'center', justifyContent:'center',
                      boxShadow:'0 4px 12px rgba(124,58,237,0.3)',
                    }}>{requiredLetter}</div>
                  </div>
                )}
                <div className="text-end">
                  <span className="text-muted small fw-bold">CURRENT WORD</span>
                  <div style={{fontSize:'1.4rem', fontWeight:900, letterSpacing:2, color:'var(--text)'}}>{current}</div>
                </div>
              </div>

              <div style={{maxHeight:200, overflowY:'auto', padding:'8px 0'}}>
                <div className="d-flex flex-wrap gap-2">
                  {chain.map((w, i) => (
                    <span key={i} className="result-badge"
                      style={i === chain.length - 1
                        ? {background:'var(--primary)', color:'#fff', fontSize:'1rem'}
                        : {opacity:0.7}}>
                      {w}
                      {i < chain.length - 1 && <span style={{marginLeft:4, opacity:0.5}}>→</span>}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {status === 'playing' ? (
              <div className="solver-card">
                <form onSubmit={handleSubmit}>
                  <label className="fw-bold mb-1">
                    Enter a word starting with <strong style={{color:'var(--primary)'}}>{requiredLetter}</strong>
                  </label>
                  <div className="d-flex gap-2">
                    <input
                      ref={inputRef}
                      className="big-input"
                      style={{textTransform:'uppercase', letterSpacing:3, flex:1}}
                      value={input}
                      onChange={e => { setInput(e.target.value.replace(/[^a-zA-Z]/g, '')); setError(''); }}
                      placeholder={`${requiredLetter}…`}
                      maxLength={20}
                      autoComplete="off"
                    />
                    <button type="submit" className="solve-btn" disabled={loading || !input}
                      style={{padding:'0.5rem 1.2rem', minWidth:90}}>
                      {loading ? <span className="spinner-border spinner-border-sm"></span> : '→ Add'}
                    </button>
                  </div>
                </form>
                {error && <div className="alert alert-danger alert-custom mt-2 mb-0">{error}</div>}
                <div className="text-center mt-3">
                  <button className="btn btn-sm text-muted" onClick={giveUp}
                    style={{fontFamily:'Nunito,sans-serif', textDecoration:'underline', background:'none', border:'none'}}>
                    Give up
                  </button>
                </div>
              </div>
            ) : (
              <div className="solver-card text-center">
                <div style={{fontSize:'2.5rem'}}>🏁</div>
                <h3 className="fw-bold">Game Over!</h3>
                <p className="text-muted">You chained <strong style={{color:'var(--primary)'}}>{chain.length}</strong> word{chain.length !== 1 ? 's' : ''}!</p>
                {chain.length >= 10 && <p className="text-success fw-bold">🔥 Impressive chain!</p>}
                {chain.length >= 20 && <p className="text-warning fw-bold">🌟 Word chain master!</p>}
                <div className="d-flex gap-2 justify-content-center flex-wrap">
                  <button className="solve-btn" onClick={startGame}>🔄 Play Again</button>
                  <button className="btn fw-bold"
                    style={{ borderRadius: 50, background: '#4f46e5', color: '#fff', border: 'none', fontFamily: 'Nunito,sans-serif', padding: '0.55rem 1.2rem' }}
                    onClick={() => setShowShare(true)}>
                    📤 Share Result
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        <SeoContent game="word-chain" />
      </div>
    </div>
  );
}
